import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import type { Recipient, RecipientSort, RecipientSortKey } from '../types';
import { RecipientsTableRow, TABLE_CHECKBOX_CLASS } from './RecipientsTableRow';
import { SortableHeader } from './SortableHeader';

interface RecipientsTableProps {
  recipients: Recipient[];
  selectedIds: Set<string>;
  headerSelectionState: boolean | 'indeterminate';
  sort: RecipientSort | null;
  hasMore: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  scrollRootRef: React.RefObject<HTMLDivElement | null>;
  onToggle: (_id: string) => void;
  onToggleSelectAll: () => void;
  onSort: (_key: RecipientSortKey) => void;
  onLoadMore: () => void;
  loading?: boolean;
}

export const RecipientsTable: React.FC<RecipientsTableProps> = ({
  recipients,
  selectedIds,
  headerSelectionState,
  sort,
  hasMore,
  sentinelRef,
  scrollRootRef,
  onToggle,
  onToggleSelectAll,
  onSort,
  onLoadMore,
  loading = false,
}) => (
  <div className="relative">
    <div
      ref={scrollRootRef}
      className="max-h-[360px] overflow-y-auto rounded-xl border border-gray-200 bg-white"
    >
      <table className="w-full table-fixed caption-bottom text-sm">
        <colgroup>
          <col className="w-[7%]" />
          <col className="w-[30%]" />
          <col className="w-[22%]" />
          <col className="w-[18%]" />
          <col className="w-[23%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-gray-200">
            <th className="h-12 px-5 pl-5 pr-2 text-left align-middle">
              <Checkbox
                checked={headerSelectionState}
                onCheckedChange={onToggleSelectAll}
                aria-label="Selecionar todos"
                className={TABLE_CHECKBOX_CLASS}
              />
            </th>
            <SortableHeader
              label="Nome e e-mail"
              sortKey="name"
              sort={sort}
              onSort={onSort}
              className="px-3"
            />
            <SortableHeader
              label="Whatsapp"
              sortKey="whatsapp"
              sort={sort}
              onSort={onSort}
              className="px-3 whitespace-nowrap"
            />
            <SortableHeader
              label="Status"
              sortKey="status"
              sort={sort}
              onSort={onSort}
              className="px-3 whitespace-nowrap"
            />
            <SortableHeader
              label="Envio realizado"
              sortKey="alreadySent"
              sort={sort}
              onSort={onSort}
              className="px-3 pr-5 whitespace-nowrap"
            />
          </tr>
        </thead>
        <tbody>
          {recipients.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-5 py-10 text-center text-gray-500">
                {loading ? 'Carregando destinatários...' : 'Nenhum destinatário encontrado'}
              </td>
            </tr>
          ) : (
            recipients.map((recipient) => (
              <RecipientsTableRow
                key={recipient.id}
                recipient={recipient}
                selected={selectedIds.has(recipient.id)}
                onToggle={onToggle}
              />
            ))
          )}
        </tbody>
      </table>
      <div ref={sentinelRef} className="h-0 w-full overflow-hidden" aria-hidden />
    </div>
    {hasMore ? (
      <>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 rounded-b-xl bg-gradient-to-t from-white to-transparent" />
        <button
          type="button"
          onClick={onLoadMore}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Mais resultados
          <ChevronDown className="h-4 w-4" />
        </button>
      </>
    ) : null}
  </div>
);
